import { useTranslation } from 'react-i18next';
import {
    useTheme,
    Box,
    Typography,
    IconButton,
    Icon,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ChartTooltip,
    ResponsiveContainer,
} from '../../../../base';
import {
    IClientSourceOfHireChartData,
    IChartTooltipProps,
    SourceOfHireDistributionMetric,
    SOURCE_OF_HIRE_DISTRIBUTION_METRICS,
    getSourceOfHireBarsBySource,
    formatSourceOfHireAmount,
    exportSourceOfHireDistributionToExcel,
} from '../../../../common';
import { Directions } from '../../../../common/consts/system.consts';
import {
    StyledBarCard,
    StyledBarCardHeader,
    StyledBarCardBody,
    StyledBarEmptyState,
} from './BarView.styles';

interface ISourceOfHireSourcesBarCardProps {
    chartData: IClientSourceOfHireChartData;
    metric: SourceOfHireDistributionMetric;
    selectedSourceTitles: string[];
}

const SourceOfHireSourcesBarCard = ({
    chartData,
    metric,
    selectedSourceTitles,
}: ISourceOfHireSourcesBarCardProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isRtl = theme.direction === Directions.rtl;

    const metricOption = SOURCE_OF_HIRE_DISTRIBUTION_METRICS.find(
        (option) => option.metric === metric
    );
    const isPercent = metricOption?.isPercent ?? false;
    const metricLabel = metricOption ? t(metricOption.shortLabelKey) : metric;

    const entries = chartData[metric] ?? [];
    const filteredEntries =
        selectedSourceTitles.length > 0
            ? entries.filter((entry) =>
                  selectedSourceTitles.includes(entry.sourceTitle)
              )
            : entries;
    const bars = getSourceOfHireBarsBySource(entries, selectedSourceTitles);
    const hasData = bars.some((bar) => bar.value > 0);

    const renderTooltipContent = ({ active, payload }: IChartTooltipProps) => {
        if (!active || !payload?.length) return null;
        return (
            <Box
                sx={{
                    backgroundColor: theme.palette.grey[600],
                    color: theme.palette.common.white,
                    padding: theme.spacing(0.5, 1.5),
                    borderRadius: 1.5,
                    ...theme.typography.caption,
                }}
            >
                {formatSourceOfHireAmount(payload[0].value, isPercent)}
            </Box>
        );
    };

    return (
        <StyledBarCard>
            <StyledBarCardHeader container>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {t('reportsPage.sourceOfHireReport.barBySourceTitle', {
                        metric: metricLabel,
                    })}
                </Typography>
                {hasData && (
                    <IconButton
                        size="small"
                        onClick={() =>
                            exportSourceOfHireDistributionToExcel(
                                filteredEntries,
                                metric,
                                t
                            )
                        }
                    >
                        <Icon name="FileDownloadOutlinedIcon" size={16} />
                    </IconButton>
                )}
            </StyledBarCardHeader>
            <StyledBarCardBody>
                {hasData ? (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart
                            data={bars}
                            margin={{
                                top: 40,
                                right: isRtl ? 5 : 40,
                                left: isRtl ? 40 : 5,
                            }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                                stroke={theme.palette.grey[300]}
                            />
                            <XAxis
                                dataKey="name"
                                reversed={isRtl}
                                tick={{
                                    fill: theme.palette.text.primary,
                                    fontSize: 12,
                                }}
                                axisLine={false}
                                tickLine={false}
                            />
                            <YAxis
                                domain={[0, 'auto']}
                                orientation={isRtl ? 'right' : 'left'}
                                tickFormatter={(value: number) =>
                                    formatSourceOfHireAmount(value, isPercent)
                                }
                                tick={{
                                    fill: theme.palette.grey[600],
                                    fontSize: 14,
                                    textAnchor: 'end',
                                }}
                                axisLine={false}
                                tickLine={false}
                                width={60}
                            />
                            <ChartTooltip
                                content={renderTooltipContent as never}
                                cursor={false}
                            />
                            <Bar
                                dataKey="value"
                                fill={theme.palette.primary.main}
                                barSize={49}
                                radius={[5, 5, 0, 0]}
                            />
                        </BarChart>
                    </ResponsiveContainer>
                ) : (
                    <StyledBarEmptyState>
                        <Icon
                            name="NoTrackableDataIconBig"
                            size={72}
                            color="primary"
                        />
                        <Typography variant="h6">
                            {t(
                                'reportsPage.sourceOfHireReport.noMetricDataTitle'
                            )}
                        </Typography>
                        <Typography
                            variant="body2"
                            color={theme.palette.grey[600]}
                        >
                            {t(
                                'reportsPage.sourceOfHireReport.noMetricDataDescription'
                            )}
                        </Typography>
                    </StyledBarEmptyState>
                )}
            </StyledBarCardBody>
        </StyledBarCard>
    );
};

export default SourceOfHireSourcesBarCard;
